import { CellPosition, Column, ICellRendererParams, StartEditingCellParams, SuppressKeyboardEventParams, TabToNextCellParams } from 'ag-grid-enterprise';
import { debounceTime, distinctUntilChanged, Observable, of, Subject, Subscription } from 'rxjs';
import { OnLeavingEditorParams } from '../../../models/base-cell-editor/on-leaving-editor-params.model';
import { PatchOperation } from '../../../../common/models/patch-operation.model';
import { PatchOperationsWithItemId } from '../../../models/patch-operations-with-item-id.model';
import { InlineEditorCellRendererComponent } from '../../renderers/inline-editor-cell-renderer/inline-editor-cell-renderer.component';
import { AlwaysActiveCheckboxCellComponent } from '../../renderers/always-active-checkbox-cell/always-active-checkbox-cell.component';
import { ImpersonateUserCellComponent } from '../../renderers/impersonate-user-cell/impersonate-user-cell.component';
import { EditHistoryCellComponent } from '../../renderers/edit-history-cell/edit-history-cell.component';
import { SendActivationEmailCellComponent } from '../../renderers/send-activation-email-cell/send-activation-email-cell.component';
import { AdminFunctionsCellComponent } from '../../renderers/admin-functions-cell/admin-functions-cell.component';


const findParentRowForEl = (el: HTMLElement, attribute: string = 'role', attributeValue: string = 'row'): HTMLElement | null => {
  let rowEl: HTMLElement | null = el;
  while (rowEl) {
      rowEl = rowEl.parentElement;
      if (rowEl && rowEl.getAttribute(attribute) === attributeValue) {
        return rowEl;
      }   
  }

  return null;
};

export class AgGridNavigationBaseClass {

  private static lastTabShift = false;
  private static tabTrackerActive = false;
  public subscription: Subscription = new Subscription();
  public selectorToFindFirstInstanceOfAndFocusAgGridCellParent: string;
  public mostRecentKey = new Subject<{ key: string, params: SuppressKeyboardEventParams }>();
  eParentEl!: HTMLElement;
  previousEL!: HTMLElement;
  parentComponent: string;
  overrideSpaceKeyHandling: boolean = false;
  overrideEnterKeyHandling: boolean = false;
  overrideEscapeKeyHandling: boolean = false;
  public patchQueue: Array<PatchOperationsWithItemId> = [];
  public patchOperationsToDo = new Subject<Array<PatchOperationsWithItemId> | undefined>();

  constructor() {
    this.selectorToFindFirstInstanceOfAndFocusAgGridCellParent = 'input';
    this.hookUpSuppressKeyboardEventHandler();

    if (!AgGridNavigationBaseClass.tabTrackerActive) {
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Tab') AgGridNavigationBaseClass.lastTabShift = e.shiftKey;
      }, true);
      AgGridNavigationBaseClass.tabTrackerActive = true;
    }

    this.subscription.add(this.patchOperationsToDo
      .pipe(
        debounceTime(2000),
        distinctUntilChanged()
      )
      .subscribe((queuedOperations) => {
        if (this.patchQueue && this.patchQueue.length > 0) {
          this.doQueuedPatchOperations(queuedOperations);
          this.patchQueue = [];
        }
      }));
  }

  /**
   * Wire to onCellFocused for grids whose cells host buttons.
   * ag-grid only passes focus on to a cell's contents while the row is editing (CellCtrl.onCellFocused
   * picks the first focusable child only when editing), so in a read only grid Tab leaves focus sitting
   * on the cell and the buttons inside it are never reached. This moves focus on to the button nearest
   * the edge the focus arrived from. Runs after ag-grid has finished focusing the cell, and no-ops when
   * the cell holds no buttons or when focus has already reached one.
   */
  public cellFocusedHandler(): void {
    setTimeout(() => {
      const cell = document.activeElement as HTMLElement;
      if (!cell?.classList?.contains('ag-cell')) {
        return;
      }
      const buttons = Array.from(cell.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
      if (buttons.length === 0) {
        return;
      }
      buttons[AgGridNavigationBaseClass.lastTabShift ? buttons.length - 1 : 0].focus();
    });
  }

  /**
   * Wire to suppressKeyboardEvent (on a column, or on defaultColDef) for grids whose cells hold more
   * than one button. Returning true leaves the Tab alone so the browser moves focus to the next button
   * in the same cell; on the first/last button it returns false so ag-grid takes Tab out of the cell.
   */
  public supressTabBetweenButtonsHandler(params: SuppressKeyboardEventParams): boolean {
    if (params.event.key !== 'Tab') {
      return false;
    }
    const target = params.event.target as HTMLElement;
    const cell = target?.closest('.ag-cell');
    if (!cell) {
      return false;
    }
    const buttons = Array.from(cell.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    const idx = buttons.indexOf(target as HTMLButtonElement);
    if (idx === -1) {
      return false;
    }
    const atEnd = !params.event.shiftKey && idx === buttons.length - 1;
    const atStart = params.event.shiftKey && idx === 0;
    return !(atEnd || atStart);
  }

  public supressSpaceAndEnterKeyboardEventHandler(params: SuppressKeyboardEventParams): boolean {
    //Only want to override space key when not in edit mode, and if the row is expandable
    if (params.event.key == " " && this.overrideSpaceKeyHandling && !params.editing && params.node.isExpandable()) {
      this.mostRecentKey.next({ key: params.event.key, params: params });
      return true;
    }

    if (params.event.key == "Enter" && this.overrideEnterKeyHandling) {
      this.mostRecentKey.next({ key: params.event.key, params: params });
      return true;
    }

    return false;
  }

  public supressTabKeyboardEventHandler(params: SuppressKeyboardEventParams): boolean {
    if (params.event.key == "Tab") {
      let rowCount = params.api.getDisplayedRowCount();
      //Check if we're either on an expanded cell or inside a detail, and

      //Default behavior here, we only care if we're on the last element of a detail
      if (rowCount != 0 && params.node.detail && params.node.rowIndex == (rowCount - 1)) {
        //this.mostRecentKey.next({ key: params.event.key, params: params });
        //find the parent row node, find it's parent and go to the next child
        let parentNodeIndex = parseInt(this.previousEL.parentElement.getAttribute("row-index"));
        let rowParent = this.previousEL.parentNode.parentElement;
        let nextRowIndex = parentNodeIndex + 1;
      //  //If there are any further rows
        if (rowParent.children.length > nextRowIndex) {
      //    let nextRowEL = rowParent.children[nextRowIndex];
      //    let ingredientEditValidatedCellRenderers = nextRowEL.querySelectorAll(this.selectorToFindFirstInstanceOfAndFocusAgGridCellParent);
      //    let agGridDiv = ingredientEditValidatedCellRenderers[0].parentElement.parentElement.parentElement;
      //    agGridDiv.classList.add("ag-has-focus");
      //    agGridDiv.classList.add("ag-cell-focus");
          //if (results && results.length > 0) {
          //  let firstCell = results[0].children[0] as HTMLElement;
          //  //firstCell.setAttribute("tabindex", "0");
          //  firstCell.focus();
          //}
          ////params.event.stopImmediatePropagation();


          ////nextRowEL.focus();
          if (params.editing) {
            params.api.stopEditing(false);
          }
          return true;
        }
      }
    }

    return false;
  }

  hookUpSuppressKeyboardEventHandler(): void {
    this.subscription.add(this.mostRecentKey
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe((mostRecentKeyEvent) => {
        //Handle overriding the space key to expand and contract group cells
        if (mostRecentKeyEvent.key == " " && mostRecentKeyEvent.params.node.isExpandable()) {
          mostRecentKeyEvent.params.node.setExpanded(!mostRecentKeyEvent.params.node.expanded);
        }
        //Handle overriding the Tab key when on the last element of the child detail component
        if (mostRecentKeyEvent.key == "Tab") {
          let previousRowEl: HTMLElement = findParentRowForEl(this.eParentEl, 'role', 'rowgroup');
          previousRowEl;
        }
        //Handle overriding the Enter key
        if (mostRecentKeyEvent.key == "Enter") {
          mostRecentKeyEvent.params.api.startEditingCell({
            rowIndex: mostRecentKeyEvent.params.node.rowIndex,
            colKey: mostRecentKeyEvent.params.colDef.colId,
            rowPinned: mostRecentKeyEvent.params.node.rowPinned
          } as StartEditingCellParams);
        }
      //  //Handle overriding the Escape key
      //  if (mostRecentKeyEvent.key == "Escape") {
      //    mostRecentKeyEvent.params.api.stopEditing(false);
      //    this.patchCellEditorValue({
      //      data: mostRecentKeyEvent.params.data,
      //      fieldToPatch: "/" + mostRecentKeyEvent.params.colDef.field,
      //      valueToPatch: mostRecentKeyEvent.params.data[mostRecentKeyEvent.params.colDef.field]
      //    } as OnLeavingEditorParams);
      //  }
      }));
  }

  hookUpHtmlElementListener(cellRendererParams: ICellRendererParams) {
    this.eParentEl = cellRendererParams.eParentOfValue;
    this.eParentEl.addEventListener('focus', this.onParentElFocus.bind(this));
  }

  removeHtmlEventListener(): void {
    if (this.eParentEl) {
      this.eParentEl.removeEventListener('focus', this.onParentElFocus.bind(this));    
    }
  }

  onParentElFocusLoss(event: FocusEvent) {
    event.currentTarget;
    event.target;
    console.log("Focus Loss event fired for: " + this.parentComponent);
  }

  onParentElFocus(event: FocusEvent) {
    const currentEl = event.target as HTMLElement;

    //bind to focusOut on the current element
   // currentEl.addEventListener('blur', this.onParentElFocusLoss.bind(this));

    this.previousEL = event.relatedTarget as HTMLElement;

    this.customFocusLogic(currentEl, this.previousEL, this.selectorToFindFirstInstanceOfAndFocusAgGridCellParent);
  }

  customFocusLogic(currentHtmlElement: HTMLElement, previousHtmlElement: HTMLElement, selectorToFindFirstInstanceOfAndFocusAgGridCellParent: string) {
    let childElementsFound = currentHtmlElement.querySelectorAll(selectorToFindFirstInstanceOfAndFocusAgGridCellParent);
    let firstRenderer = childElementsFound[0];
    
    let currentRow = currentHtmlElement && parseInt(currentHtmlElement.getAttribute('row-index')!, 10);
    let previousRowEl: HTMLElement = findParentRowForEl(previousHtmlElement, 'role', 'row');
    let previousRow = previousRowEl && parseInt(previousRowEl.getAttribute('row-index')!, 10);

    this.childElementFocusProcessing(childElementsFound, !previousRow || currentRow >= previousRow);
  }
  
  getItemId(data: any): string {
    return "";
  }

  patchCellEditorValue(params: OnLeavingEditorParams): void {

    //this.mainService.showLoading({ loading: true } as LoaderData);
    if (params.patchInfo && params.patchInfo.length > 0) {
      let itemId = this.getItemId(params.data);
      
      params.patchInfo.forEach(x => {
        let existingItem = this.patchQueue.filter(x => x.itemId == itemId);
        let patchOperation = {
          path: x.fieldToPatch,
          value: x.valueToPatch,
          op: "replace",
        } as PatchOperation;

        if (existingItem && existingItem.length > 0) {
          let existingFieldPatchOperation = existingItem[0].patchOperations.filter(y => y.path == x.fieldToPatch);

          //Already have a queued value for this field, replace it with newest value
          if (existingFieldPatchOperation && existingFieldPatchOperation.length > 0) {
            existingFieldPatchOperation[0].value = x.valueToPatch;
          }
          else {
            existingItem[0].patchOperations.push(patchOperation);
          }
        }
        else {
          this.patchQueue.push({
            itemId: itemId,
            patchOperations: [patchOperation]
          } as PatchOperationsWithItemId);
        }
      });

      this.patchOperationsToDo.next(this.patchQueue);
    }
  }


  doQueuedPatchOperations(patchOps: Array<PatchOperationsWithItemId>, returnObservables: boolean = false): Array<Observable<any>> | void {
    if (returnObservables) {
      return [of(null)];
    }
  }

  childElementFocusProcessing(elements: NodeListOf<Element>, navigatingForwards: boolean = true): void {
    if (navigatingForwards) {
      // Focus on the first input
      let focusElement = elements[0].parentElement;
      focusElement.focus();
    } else {
      // Navigating backwards
      // Focus on the last element
      let focusElement = elements[elements.length - 1].parentElement;
      focusElement.focus();
    }
  }

  tabToNextCellHandler(params: TabToNextCellParams): CellPosition | boolean {
    // Find the next editable cell in the row (or next row)
    let col = params.nextCellPosition.column;
    let colDef = col.getColDef();
    let rowIndex = params.nextCellPosition.rowIndex;

    // Helper to check if a cell is editable
    const isEditable = (col: Column<any>, rowIndex: number) => {
      const colDef = col.getColDef();
      if (typeof colDef.editable === 'function') {
        // If editable is a function, call it with params
        return colDef.editable({
          column: col,
          colDef: colDef,
          context: params.context,
          api: params.api,
          data: params.api.getDisplayedRowAtIndex(rowIndex).data,
          node: params.api.getDisplayedRowAtIndex(rowIndex),
        });
      }
      return !!colDef.editable;
    };

    // Move to the next cell until an editable one (or the inline editor cell renderer) is found or end of grid
    while (col && (!isEditable(col, rowIndex) && colDef.cellRenderer != InlineEditorCellRendererComponent
      && colDef.cellRenderer != AlwaysActiveCheckboxCellComponent && colDef.cellRenderer != ImpersonateUserCellComponent
      && colDef.cellRenderer != EditHistoryCellComponent && colDef.cellRenderer != SendActivationEmailCellComponent
      && colDef.cellRenderer != AdminFunctionsCellComponent)) {

      // Move to next/previous column
      const allColumns = params.api.getAllDisplayedColumns();
      let colIndex = allColumns.indexOf(col);
      colIndex = params.backwards ? colIndex - 1 : colIndex + 1;

      if (colIndex < 0 || colIndex >= allColumns.length) {
        // Move to next/previous row
        rowIndex = params.backwards ? rowIndex - 1 : rowIndex + 1;
        if (rowIndex < 0 || rowIndex >= params.api.getDisplayedRowCount()) {
          return null; // No more cells
        }
        colIndex = params.backwards ? allColumns.length - 1 : 0;
      }
      col = allColumns[colIndex];
      colDef = col.getColDef();
    }

    if (col) {
      let targetNote = params.api.getDisplayedRowAtIndex(rowIndex);
      return {
        rowIndex,
        column: col,
        rowPinned: targetNote.rowPinned
      } as CellPosition;
    }
    return null;
  }
}
